import { useState } from 'react';
import type { ErrorField } from '../types/ErrorField';
import type { AddLegalAgreementValue } from './useLegalCaseAgreements';

// Campos com binding próprio na tela; qualquer outro 422 do backend cai no bloco residual.
const INLINE_FIELDS = new Set(['agreedAmount', 'signedAt', 'description']);

export const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

// O valor acordado usa CurrencyInput (value numérico); 0 = "não informado" (o form começa
// zerado), então não é enviado — coerente com o "Valor da Causa" do cadastro do caso.
const moneyOrUndefined = (value: number): number | undefined =>
    value && value > 0 ? value : undefined;

interface UseLegalCaseAgreementFormArgs {
    // Compartilhado pela criação e pela renegociação (superseção): os dois enviam os mesmos
    // campos do acordo (valor, data, observações), sem status.
    onSubmit: (value: AddLegalAgreementValue) => Promise<boolean>;
}

// Estado e handlers do formulário de acordo, fora do componente (diretriz do repo: sem função
// dentro de função). O acordo nasce em formalização (status não é campo do form de criação).
// Centraliza o reset ao enviar com sucesso.
export default function useLegalCaseAgreementForm({ onSubmit }: UseLegalCaseAgreementFormArgs) {
    const [agreedAmount, setAgreedAmount] = useState(0);
    const [signedAt, setSignedAt] = useState('');
    const [description, setDescription] = useState('');

    const reset = () => {
        setAgreedAmount(0);
        setSignedAt('');
        setDescription('');
    };

    // O backend aceita acordo sem valor (fica a confirmar), então o botão fica sempre
    // habilitado — o feedback vem do backend, como nas demais seções.
    const canSubmit = true;

    const submit = async () => {
        const ok = await onSubmit({
            agreedAmount: moneyOrUndefined(agreedAmount),
            signedAt: signedAt.trim() || undefined,
            description: description.trim() || undefined,
        });
        if (ok) reset();
    };

    return {
        agreedAmount, setAgreedAmount,
        signedAt, setSignedAt,
        description, setDescription,
        canSubmit, submit, reset,
    };
}
